import { storage } from './storage';
import { STORAGE_KEYS } from '../constants/storageKeys';
import type { Prompt, Folder } from '../types';

/**
 * Delete a folder AND the prompts inside it, in the live storage schema
 * (prompt.folder holds the folder id).
 *
 * Every view must use this so deletion semantics don't diverge: removing only
 * the folder record strands its prompts under a dead folder id — invisible in
 * "Unsorted" (whose filter is `!prompt.folder`) and only reachable via "All
 * Prompts". Deleting the prompts with the folder matches the confirmation
 * dialog's warning and the domain service's documented behavior.
 *
 * Deliberately NOT routed through the repository/domain layer: those filter on
 * `folderId`/`updatedAt`, fields the live data doesn't have, so their
 * `countByFolder` is always 0 against real data (see issue #68).
 */
export async function deleteFolderWithPrompts(
  folderId: string
): Promise<{ deletedPrompts: number }> {
  const prompts = ((await storage.get(STORAGE_KEYS.PROMPTS)) ?? []) as Prompt[];
  const folders = ((await storage.get(STORAGE_KEYS.FOLDERS)) ?? []) as Folder[];

  const remainingPrompts = prompts.filter(p => p.folder !== folderId);
  const remainingFolders = folders.filter(f => f.id !== folderId);
  const deletedPrompts = prompts.length - remainingPrompts.length;

  if (deletedPrompts > 0) {
    await storage.set(STORAGE_KEYS.PROMPTS, remainingPrompts);
  }
  if (remainingFolders.length !== folders.length) {
    await storage.set(STORAGE_KEYS.FOLDERS, remainingFolders);
  }

  return { deletedPrompts };
}
